import { LEVEL } from '../hooks/usePermission';
import { routeAllowedForFacility } from '../utils/basicFacility';
import { featureOn, type PurchasingFacilityFeatures, type PurchasingFeature } from '../api/purchasing/features';
import { MENU_CHANGES_ROUTE, MENU_CHANGES_TABS, NEW_MENU_ROUTE, type MenuTabGate } from './menuPages';

/**
 * The site's navigation, in one place (Jeff, 2026-10-20).
 *
 * Every page a user moves between belongs to exactly one SECTION, and within it to one GROUP (a page
 * may also be listed in a second section, marked `crossListed`: see NavPageConfig). The
 * sidebar draws the sections as the rail and the current section's groups as the panel beside it;
 * each section's overview page, the breadcrumbs, the phone More sheet, the command palette and the
 * Feature Access dialog all read this same list, so they cannot disagree about what exists or who
 * may open it.
 *
 * A page's `securityField` / `minLevel` / `psiOnly` / `licence` must match its route guard in
 * App.tsx (navConfig.test.ts checks every entry against the route), so the menu never offers a page
 * the route would turn away and never hides one it would open.
 */

/** Facility conditions a page can depend on, on top of the role. */
export type NavCondition =
  /** Only for a corporate facility (the corporate dashboard). */
  | 'corporate'
  /** The facility has the kitchen display turned on (KDS_Enabled). */
  | 'kds'
  /** Not offered to a Basic facility (UseProduction = 0), as WinForms does not. */
  | 'production'
  /** Hidden when the facility does not use assessments (UseAssessment = 0). */
  | 'assessments'
  /**
   * Hidden under an Assessment Only licence (PM_Allowed_Modules.Dietech_Assessment_Only, read by
   * /api/TablesMenu), as WinForms leaves its node out of the menu: the Recipes nodes and Items >
   * Linking (Fill_MiscTRL, frmDietech_Resident_DataGrid_V2.vb:6729-6735, 6808-6811).
   */
  | 'notAssessmentOnly'
  /** Waits for facility settings, then shows greyed out with the reason when EHR is off. */
  | 'ehr';

/** Labels a facility words its own way. */
export type NavLabelKey = 'residentPlural' | 'manageResidents' | 'globalMealPattern' | 'ehrTitle';

export interface NavPageConfig {
  label: string;
  /** Replaces `label` with the facility's own wording. */
  dynamicLabel?: NavLabelKey;
  /** Where the link goes. `{facilityId}` is filled with the current facility. */
  route: string;
  /** Used when `route` has a token that cannot be filled (no facility selected). */
  fallbackRoute?: string;
  /** Other paths that are this page, for the active state (variants and old addresses). */
  alsoMatches?: string[];
  /** One plain sentence: what the page is for. `{residents}` becomes the facility's word. */
  description: string;
  /** Role column the route is secured by (App.tsx). Omitted = open to every signed-in user. */
  securityField?: string;
  /** Minimum level of `securityField`. Defaults to VIEW (1). */
  minLevel?: number;
  /** PSI staff only (a psibuffalo.com login), whatever the role allows. */
  psiOnly?: boolean;
  /**
   * PSI staff may open it too, whatever the role allows (the route's securedOrPsi, the API's
   * RequirePsiStaffOrRoleLevel). Everyone else needs `securityField` at `minLevel`.
   */
  psiStaffToo?: boolean;
  /**
   * A page of tabs with a gate each (Menu Changes, navigation/menuPages.ts): shown when any of them
   * lets the user in. The route has no gate of its own; the page shows only the tabs allowed.
   */
  anyOf?: MenuTabGate[];
  /** A paid module the facility must be licensed for in Tech Log. */
  licence?: 'purchasing';
  /**
   * An optional purchasing feature the facility must have switched on in Purchasing Setup
   * (several: any of them). Off while the switches load.
   */
  purchasingFeature?: PurchasingFeature | PurchasingFeature[];
  when?: NavCondition[];
  /** Extra words the command palette matches on. */
  keywords?: string;
  /** Feature Access cannot hide it (the corporate dashboard, as before). */
  alwaysShown?: boolean;
  /**
   * A second listing of a page whose home is another section (Selective Touch is Kitchen's, and is
   * also listed under Residents; Jeff, 2026-10-24). Same route, label and gates as the home entry.
   * Its address belongs to the home section (rail, panel, breadcrumb) unless the user opened it from
   * this listing, for as long as they stay on the page (navEntry.ts). The top-bar search lists it
   * once, under its home; pins, Recent and Feature Access hides go by the route, so they are shared.
   */
  crossListed?: boolean;
}

export interface NavGroupConfig {
  /** Empty for a section whose pages need no heading (Home). */
  title: string;
  pages: NavPageConfig[];
}

export interface NavSectionConfig {
  id: string;
  label: string;
  dynamicLabel?: NavLabelKey;
  /** Bootstrap icon for the rail. Sections only: pages carry no icons. */
  icon: string;
  /**
   * The section's main page (a page route in its groups): what the rail opens. When the user cannot
   * open it, the rail opens the first page in the section they can.
   */
  mainRoute: string;
  /**
   * A landing page that lists the section's pages. When set, the rail opens it instead of the main
   * page: Setup opens its list of setup pages, never a settings page someone could change by
   * accident (Jeff, 2026-10-21: "it should be what it was before, with the multiple options").
   * Kitchen does the same (2026-10-23). App.tsx serves it with <SectionHome section="..." />.
   */
  homeRoute?: string;
  /** Gate on the section as a whole, on top of each page's own. */
  securityField?: string;
  licence?: 'purchasing';
  groups: NavGroupConfig[];
}

const PURCHASING = { licence: 'purchasing' as const };

/** Kitchen's page, also listed under Residents > Tracking (crossListed). One definition, so the two cannot drift. */
const SELECTIVE_TOUCH: NavPageConfig = {
  label: 'Selective Touch',
  route: '/selective-touch',
  description: "Take {residents}' menu choices on a touch screen.",
  securityField: 'dietech_Selective_Menu_Touch_Level',
  when: ['production'],
};

export const NAV_SECTIONS: NavSectionConfig[] = [
  {
    id: 'home',
    label: 'Home',
    icon: 'bi-house',
    mainRoute: '/dashboard',
    groups: [
      {
        title: '',
        pages: [
          { label: 'Dashboard', route: '/dashboard', description: "See today's admissions, discharges, diet and allergy changes for your facility." },
          {
            label: 'Corporate Dashboard',
            route: '/corporate-dashboard',
            description: 'Compare census, diets and activity across the facilities in your organization.',
            // Cross-facility figures are reporting: the route and CorporateDashboardController ask for Reports 1.
            securityField: 'dietech_Report_Level',
            when: ['corporate'],
            alwaysShown: true,
          },
        ],
      },
    ],
  },
  {
    id: 'residents',
    label: 'Residents',
    dynamicLabel: 'residentPlural',
    icon: 'bi-people',
    mainRoute: '/people',
    groups: [
      {
        title: 'Records',
        pages: [
          {
            label: 'Residents',
            dynamicLabel: 'residentPlural',
            route: '/people',
            description: 'Look up {residents}, open their records and change diets, allergies and meal patterns.',
            securityField: 'dietech_Resident_Level',
            keywords: 'patients guests people',
          },
          // Jeff, 2026-10-23: "add EHR under resident". The EHR sends admissions and diet orders for
          // {residents}, so it sits with their records; it was under Clinical > Review.
          {
            label: 'EMR/EHR',
            dynamicLabel: 'ehrTitle',
            route: '/emr',
            description: 'Review the admissions and diet orders sent from your EHR.',
            securityField: 'dietech_Ehr_Level',
            when: ['ehr'],
            keywords: 'emr ehr hl7 pcc pointclickcare',
          },
          {
            label: 'Manage Residents',
            dynamicLabel: 'manageResidents',
            // The WinForms Manage Residents form: deleted residents only, with Restore, Permanently
            // Delete, their history and EHR history. Gated on Managed Residents, as WinForms is.
            route: '/people/deleted',
            description: 'Restore or permanently delete deleted {residents}, and see their history.',
            securityField: 'dietech_Managed_Residents',
          },
          { label: 'Census', route: '/census', description: 'Review and adjust the facility census.', securityField: 'dietech_Facility_Level' },
          { label: 'Guests', route: '/people/guests', securityField: 'dietech_Resident_Level', description: 'See every guest, past and upcoming, and open their profile.' },
        ],
      },
      {
        title: 'Tracking',
        pages: [
          { label: 'Weight Entry', route: '/weight-entry', description: 'Enter weights for many {residents} at once and see who has changed.', securityField: 'dietech_Weight_History_Level' },
          { label: 'Intake Studies', route: '/intake-studies', description: 'Record how much {residents} eat over a study period.', securityField: 'dietech_Intake_Study_Level' },
          // Jeff, 2026-10-24: Selective Touch under Residents as well as Kitchen. Its home stays Kitchen.
          { ...SELECTIVE_TOUCH, crossListed: true },
          { label: 'Credits', route: '/credit-process', description: "Post each month's meal credits and bill the rest.", securityField: 'dietech_Credit_Level', keywords: 'credit process end of month' },
        ],
      },
    ],
  },
  {
    id: 'clinical',
    label: 'Clinical',
    icon: 'bi-heart-pulse',
    mainRoute: '/assessments',
    groups: [
      {
        title: 'Documentation',
        pages: [
          { label: 'Assessments', route: '/assessments', description: 'Start, continue and sign nutritional assessments.', securityField: 'dietech_Nutritional_Assessment_Level', when: ['assessments'] },
          { label: 'Plan of Care', route: '/plan-of-care', description: 'Write and update nutrition care plans and their evaluations.', securityField: 'dietech_Plan_of_Care_Data_Entry_Level' },
          { label: 'Plan of Care Setup', route: '/plan-of-care/configuration', description: 'Set the wording and signature lines used on care plans.', securityField: 'dietech_Plan_of_Care_Backend_Tables_Level' },
        ],
      },
      {
        title: 'Review',
        pages: [
          {
            label: 'Nutritional Analysis',
            route: '/nutritional-analysis',
            alsoMatches: ['/analysis/nutritional'],
            description: 'Check the nutrients a menu provides against the targets for each diet.',
            securityField: 'dietech_Analysis_Level',
          },
          { label: 'Validation', route: '/nutritional-validation', description: 'Check that the items on the menu in use are linked to the right recipes and data.', securityField: 'dietech_Nutritional_Assessment_Level' },
          { label: 'Nutrition Compliance', route: '/pdpm', description: 'Nutrition risks, documentation readiness and MDS Section K review for {residents}.', securityField: 'dietech_Pdpm_Level', keywords: 'pdpm optimizer nutrition compliance mds section k weight loss documentation readiness' },
        ],
      },
    ],
  },
  {
    id: 'menus',
    label: 'Menus',
    icon: 'bi-calendar3',
    mainRoute: '/menucycle',
    groups: [
      {
        title: 'Menu Cycles',
        pages: [
          {
            label: 'Menu Cycles',
            route: '/menucycle',
            description: 'Build and edit the menu cycles your kitchen serves.',
            securityField: 'dietech_Menu_Cycle_Level',
            // The Recycle Bin is the page's Deleted menus button now (Jeff, 2026-11-04).
            keywords: 'recycle bin deleted menus restore',
          },
          /*
           * Asking PSI for a change, applying returned extension sheets and (PSI staff) changing a menu
           * with AI, as tabs (navigation/menuPages.ts). Were Request a Menu Change, Change Requests and
           * Menu Builder's Change a menu (Jeff, 2026-11-04: "too many options").
           */
          {
            label: 'Menu Changes',
            route: MENU_CHANGES_ROUTE,
            description: 'Ask PSI to change your menu, or apply the changes marked on returned extension sheets.',
            anyOf: MENU_CHANGES_TABS.map((t) => t.gate),
            keywords: 'request a menu change requests swap psi extension sheets',
          },
          /* Importing a vendor's menu and drafting one with AI: PSI staff only, the import included (Jeff, 2026-11-04). */
          {
            label: 'New Menu',
            route: NEW_MENU_ROUTE,
            description: "Import a vendor's menu or draft a new menu cycle with AI, then review it before anything is saved.",
            securityField: 'dietech_Menu_Cycle_Level',
            psiOnly: true,
            keywords: 'menu import vendor package builder ai draft remembers memory',
          },
        ],
      },
      {
        title: 'Meal Patterns',
        pages: [
          {
            label: 'Global Meal Pattern',
            dynamicLabel: 'globalMealPattern',
            route: '/global-meal-patterns',
            description: 'Set the meal pattern items {residents} on each diet receive.',
            securityField: 'dietech_Global_Meal_Pattern_Adjustment_Level',
            when: ['production'],
          },
          { label: 'Global Adjustments', route: '/global-adjustments', description: 'Replace or remove items for everyone in a group at a mealtime.', securityField: 'dietech_Global_Meal_Pattern_Adjustment_Level' },
          // The global meal pattern groups (BEV HOT, BF ITEMS, CEREAL...), beside the Global Meal Pattern
          // page that sorts its items into them. Was "Meal Patterns" under Food Data > Reference, which
          // read as another meal pattern editor (Jeff, 2026-10-23).
          {
            label: 'Meal Pattern Groups',
            route: '/meal-patterns',
            description: 'Keep the groups the global meal patterns are sorted into.',
            securityField: 'dietech_Meal_Pattern_Level',
            keywords: 'groups meal pattern categories',
          },
        ],
      },
      {
        title: 'Menu Tools',
        pages: [
          { label: 'Special Events', route: '/special-events', description: 'Plan event meals with their date, place, guests and delivery times.', securityField: 'dietech_Special_Event_Level' },
          { label: 'Auto Item Mods', route: '/menucycle/aim', description: 'Set rules that change, allow or leave out an item for a diet order.', securityField: 'dietech_Menu_Cycle_Level', keywords: 'aim' },
          { label: 'Excel Diet Orders', route: '/menucycle/excel-diet-orders', description: 'Choose which diet orders get their own column on the Excel extension sheet.', securityField: 'dietech_Menu_Cycle_Level' },
        ],
      },
    ],
  },
  {
    id: 'food-data',
    label: 'Food Data',
    icon: 'bi-egg-fried',
    mainRoute: '/items',
    groups: [
      {
        title: 'Items & Recipes',
        pages: [
          { label: 'Items', route: '/items', description: 'Add and edit the food items, costs and nutrients your menus use.', securityField: 'dietech_Items_Level' },
          // WinForms Recipes > List; left out under an Assessment Only licence (.vb:6729-6730).
          { label: 'Recipes', route: '/recipes', description: 'Add and edit recipes, their ingredients and portions.', securityField: 'dietech_Recipe_Level', when: ['notAssessmentOnly'] },
          { label: 'USDA Matcher', route: '/items/usda-matcher', description: 'Match your items to USDA foods so their nutrients fill in.', securityField: 'dietech_Items_Level' },
          // WinForms Items > Linking (frmDietech_Resident_DataGrid_V2.vb:6805-6811). Also Tables > Item Linking.
          // Left out under an Assessment Only licence (.vb:6808), as the page refuses then too.
          {
            label: 'Item Linking',
            route: '/items/linking',
            description: 'Link each item to its recipe or tube feed product.',
            securityField: 'dietech_Items_Level',
            keywords: 'linking link items recipes tube feed products',
            when: ['notAssessmentOnly'],
          },
          {
            label: 'Order Guide/Shelf Assignment',
            route: '/items/order-guide-assignment',
            description: 'Put items and ingredients on order guides and shelf locations.',
            securityField: 'dietech_Purchasing_Order_Guides_Assignment_Level',
            keywords: 'order guide shelf location storage purchasing',
          },
        ],
      },
      {
        title: 'Reference',
        pages: [
          { label: 'Tables', route: '/tables', description: 'Edit diets, consistencies, allergies and the other lists used everywhere.', securityField: 'dietech_Table_Level', keywords: 'lookup tables' },
          { label: 'Diet Configuration', route: '/diet-config', description: 'Go straight to the diet, consistency, allergy and special needs tables.', securityField: 'dietech_Diet_Type_Level' },
        ],
      },
    ],
  },
  {
    id: 'kitchen',
    label: 'Kitchen',
    icon: 'bi-cup-hot',
    mainRoute: '/tray-line-setup',
    // Jeff, 2026-10-23: "bring back the menu selection like this for kitchen, like we did for the
    // settings". /kitchen itself is the full-screen Kitchen Display, so the list has its own address.
    homeRoute: '/kitchen-home',
    groups: [
      {
        title: 'Meal Service',
        pages: [
          { label: 'Tray Line Setup', route: '/tray-line-setup', description: 'Put rooms, beds and {residents} in tray line order.', securityField: 'dietech_Tray_Line_Level' },
          { label: 'Kitchen Display', route: '/kitchen', description: 'Show trays and orders on the kitchen screens.', securityField: 'dietech_Kitchen_Display_Level', when: ['kds'], keywords: 'kds' },
          SELECTIVE_TOUCH,
          { label: 'Bulk Order', route: '/bulk-order', description: 'Set the quantities on bulk orders for each date and meal.', securityField: 'dietech_Menu_Cycle_Level' },
          // The desktop's Forecasting screen (ucDietech_Prd_Forecasting): the extra servings the
          // Production Tally adds per date and meal. Production facilities only, as in WinForms
          // (frmDietech_Resident_DataGrid_V2.vb:6780-6793).
          {
            label: 'Forecasting',
            route: '/forecasting',
            description: 'Set the extra servings the Production Tally adds for each date and meal.',
            securityField: 'dietech_Forecasting_Level',
            when: ['production'],
            keywords: 'forecast production tally counts',
          },
        ],
      },
      {
        title: 'Staff & Rooms',
        pages: [
          { label: 'Dining Rooms', route: '/dining-rooms', description: 'Keep the list of dining rooms.', securityField: 'dietech_Dining_Room_Layout_Level' },
          { label: 'Employees', route: '/employees', description: 'Keep the list of employees who are served meals.', securityField: 'dietech_Employee_Level' },
          { label: 'Workflows', route: '/workflows', description: "Check off the day's kitchen tasks and set up the checklists.", securityField: 'dietech_Level' },
        ],
      },
    ],
  },
  {
    id: 'purchasing',
    label: 'Purchasing',
    icon: 'bi-bag',
    // Jeff, 2026-10-20: "Purchasing still seems way too complicated." Six core pages, always on;
    // every other page belongs to an optional feature the facility switches on in Purchasing Setup.
    mainRoute: '/ordering/order-guides',
    securityField: 'pur_Level',
    ...PURCHASING,
    groups: [
      {
        title: '',
        pages: [
          { label: 'Order Guides', route: '/ordering/order-guides', description: 'Keep the lists of items you order from each supplier.', securityField: 'pUR_SO_Level', ...PURCHASING },
          { label: 'Purchase Orders', route: '/ordering/orders', description: 'Create, send and track orders to your suppliers.', securityField: 'pUR_SO_Level', ...PURCHASING },
          { label: 'Suppliers', route: '/ordering/suppliers', alsoMatches: ['/ordering/contacts'], description: 'Keep supplier accounts, contacts and ordering details.', securityField: 'pur_Level', ...PURCHASING },
          {
            label: 'Item Links',
            route: '/ordering/item-links',
            description: 'Link your items to the products your suppliers sell.',
            securityField: 'pur_Level',
            ...PURCHASING,
            purchasingFeature: ['planning', 'analytics'],
          },
          {
            label: 'Price Updates',
            route: '/ordering/pricing-import',
            alsoMatches: ['/ordering/catalog'],
            description: "Load your suppliers' price files and compare their prices.",
            securityField: 'pur_Level',
            ...PURCHASING,
            keywords: 'pricing import catalog prices',
          },
          { label: 'Spend', route: '/ordering/spend', description: 'See what you spend by supplier, category and month.', securityField: 'pur_Level', ...PURCHASING },
          { label: 'Setup', route: '/ordering/setup', description: 'Set the purchasing options and switch on the features this facility uses.', securityField: 'pUR_Tables_Level', ...PURCHASING, keywords: 'purchasing setup features' },
          // Setup's Departments and Ledgers tabs are the web module's own
          // tblPur_Department / tblPur_Ledger (Jeff, 2026-10-24). The desktop's
          // tblOP_ lists are at /purchasing/departments and /purchasing/ledgers,
          // which have no menu entry of their own.
        ],
      },
      {
        title: 'Receiving & Invoices',
        pages: [
          { label: 'Receiving', route: '/ordering/receiving', description: 'Check in deliveries against their purchase orders.', securityField: 'pUR_Inv_Level', ...PURCHASING, purchasingFeature: 'receiving' },
          { label: 'Vendor Invoices', route: '/ordering/invoices', description: 'Enter supplier invoices and match them to what was received.', securityField: 'pUR_Inv_Level', ...PURCHASING, purchasingFeature: 'receiving' },
          { label: 'Credit Memos', route: '/ordering/credit-memos', description: 'Record supplier credits for returns, shortages and damage.', securityField: 'pUR_Inv_Level', ...PURCHASING, purchasingFeature: 'receiving' },
        ],
      },
      {
        title: 'Inventory & Waste',
        pages: [
          { label: 'Inventory', route: '/ordering/inventory', description: 'See what is on hand, count stock and record transactions.', securityField: 'pUR_Inv_Level', ...PURCHASING, purchasingFeature: 'inventory' },
          { label: 'Inventory Planning', route: '/ordering/inventory-planning', description: 'Set par levels and reorder points for stocked items.', securityField: 'pur_Level', ...PURCHASING, purchasingFeature: 'inventory' },
          { label: 'Waste', route: '/ordering/waste', description: 'Record food thrown away and why.', securityField: 'pUR_Inv_Level', ...PURCHASING, purchasingFeature: 'inventory' },
          // The desktop's Order Forms > Inventory > Meal Cost Summary. With Inventory off, Spend's Census
          // breakdown still gives purchases per resident day.
          {
            label: 'Meal Cost',
            route: '/ordering/meal-cost',
            description: 'Work out food cost per resident day and per meal from inventory, purchases and census.',
            securityField: 'pUR_Inv_Level',
            ...PURCHASING,
            purchasingFeature: 'inventory',
            keywords: 'meal cost summary food cost per resident day',
          },
        ],
      },
      {
        title: 'Planning',
        pages: [
          { label: 'Reorder', route: '/ordering/reorder', description: 'See what is below its reorder point and order it.', securityField: 'pUR_SO_Level', ...PURCHASING, purchasingFeature: 'planning' },
          { label: 'Demand Planning', route: '/ordering/demand', description: 'Work out how much to order from the upcoming menus and census.', securityField: 'pUR_SO_Level', ...PURCHASING, purchasingFeature: 'planning' },
          { label: 'Requisitions', route: '/ordering/requisitions', description: 'Request items from the storeroom or from purchasing.', securityField: 'pUR_SO_Level', ...PURCHASING, purchasingFeature: 'planning' },
        ],
      },
      {
        title: 'Contracts & EDI',
        pages: [
          { label: 'Contracts', route: '/ordering/contracts', description: 'Track contract prices and the dates they apply.', securityField: 'pur_Level', ...PURCHASING, purchasingFeature: 'contractsEdi' },
          { label: 'EDI', route: '/ordering/edi', description: 'See the electronic orders and invoices sent to and from suppliers.', securityField: 'pur_Level', ...PURCHASING, purchasingFeature: 'contractsEdi' },
        ],
      },
      {
        title: 'Analytics',
        pages: [
          { label: 'Costing', route: '/ordering/costing', description: 'See what your menus and recipes cost to serve.', securityField: 'pur_Level', ...PURCHASING, purchasingFeature: 'analytics' },
          { label: 'Prep Schedule', route: '/ordering/prep-schedule', description: 'Plan what to prepare each day from the menu and census.', securityField: 'pur_Level', ...PURCHASING, purchasingFeature: 'analytics' },
          { label: 'Allergens', route: '/ordering/allergens', description: 'See which purchased products contain allergens.', securityField: 'pur_Level', ...PURCHASING, purchasingFeature: 'analytics' },
          { label: 'Supplier Scorecard', route: '/ordering/scorecard', description: 'Rate suppliers on delivery, accuracy and price.', securityField: 'pur_Level', ...PURCHASING, purchasingFeature: 'analytics' },
          { label: 'Corporate', route: '/ordering/corporate', description: 'Compare purchasing across the facilities in your organization.', securityField: 'pur_Level', ...PURCHASING, purchasingFeature: 'analytics' },
        ],
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'bi-file-earmark-bar-graph',
    mainRoute: '/reports',
    groups: [
      {
        title: 'Reports',
        pages: [
          { label: 'Reports', route: '/reports', alsoMatches: ['/reports/all', '/reports/run'], description: 'Find and run reports, and open the ones that are ready.', securityField: 'dietech_Report_Level' },
          { label: 'Print Queue', route: '/reports/print-queue', description: 'See what is waiting to print and print it again.', securityField: 'dietech_Report_Level' },
          { label: 'Scheduled Reports', route: '/reports/scheduled', description: 'Set reports to run and print on a schedule.', securityField: 'dietech_Report_Level' },
          { label: 'Surveys', route: '/surveys', description: 'Build satisfaction surveys, collect the answers and see the results.', securityField: 'dietech_Survey_Level' },
        ],
      },
      {
        // Jeff, 2026-10-25: he could not find the Analysis screens. The seven WinForms screens are
        // now one page (topics), so one entry; their old addresses open it on their topic.
        title: 'Analysis',
        pages: [
          {
            label: 'Analysis',
            route: '/analysis',
            alsoMatches: [
              '/analysis/diet-orders', '/analysis/food-allergies', '/analysis/special-needs', '/analysis/special-devices',
              '/analysis/served-meals', '/analysis/served-items', '/analysis/employee-served-items',
            ],
            description: 'Compare diet orders, allergies, needs, devices and served meals across centers and units.',
            securityField: 'dietech_Analysis_Level',
            keywords: 'diet orders food allergies special needs special devices adaptive eating devices served meals served items employee served items pivot compare centers',
          },
        ],
      },
      {
        title: 'Cost & Planning',
        pages: [
          { label: 'Menu Cost', route: '/analysis/cost-menu', description: 'See what a menu costs by day and meal.', securityField: 'dietech_Analysis_Level' },
          { label: 'Nourishment Cost', route: '/analysis/cost-nourishment', description: 'See what nourishments cost.', securityField: 'dietech_Analysis_Level' },
          { label: 'Demand Forecasting', route: '/analysis/demand-forecasting', description: 'Forecast meal counts by diet from past service.', securityField: 'dietech_Analysis_Level' },
        ],
      },
    ],
  },
  {
    id: 'setup',
    label: 'Setup',
    icon: 'bi-gear',
    mainRoute: '/facilities/{facilityId}',
    homeRoute: '/settings',
    groups: [
      {
        title: 'Facility',
        pages: [
          {
            label: 'Facility Settings',
            route: '/facilities/{facilityId}',
            fallbackRoute: '/facilities',
            description: 'Change mealtimes, tray tickets, the kitchen display and the other options for this facility.',
            securityField: 'dietech_Facility_Level',
          },
          { label: 'Facilities', route: '/facilities', description: 'Add and manage the facilities on this connection.', securityField: 'dietech_Facility_Level' },
          { label: 'Unit Rooms', route: '/unit-rooms', description: 'Set up units, rooms and beds.', securityField: 'dietech_Facility_Level' },
          { label: 'Printers', route: '/printers', description: 'Set up print agents and the printers reports go to.', securityField: 'dietech_Facility_Level' },
          // WinForms shows Ticket Print Schedule only above Facility level 1 (ucDietech_Facility.vb:331).
          { label: 'Print Schedules', route: '/print-schedules', description: 'Set ready times, automatic printing and order cutoffs for tray tickets.', securityField: 'dietech_Facility_Level', minLevel: LEVEL.EDIT },
        ],
      },
      {
        title: 'Users & Access',
        pages: [
          // Users are managed in Tech Log II; the pages are PSI staff only here, as the routes are.
          { label: 'Users', route: '/users', description: 'Create and manage user accounts.', securityField: 'user_Level', minLevel: LEVEL.USER_MGMT, psiOnly: true, keywords: 'user list accounts logins' },
          { label: 'Deleted Users', route: '/users/deleted', description: 'Review and restore removed user accounts.', securityField: 'user_Level', minLevel: LEVEL.USER_MGMT, psiOnly: true },
          // The desktop's Users Logged In right, or PSI staff, as the route and the API (Jeff, 2026-11-03).
          { label: 'Logged In Users', route: '/users/logged-in', description: 'See who is signed in now; stale sign-ins end on their own after 3 hours.', securityField: 'user_Users_Logged_In_Level', psiStaffToo: true, keywords: 'users logged in sessions online signed in' },
          { label: 'Roles', route: '/roles', description: 'Set what each security role can see and change.', securityField: 'roles_Level' },
          { label: 'My Settings', route: '/user-settings', description: 'Change your own appearance, defaults and notifications.', keywords: 'user settings preferences' },
        ],
      },
      {
        title: 'System',
        pages: [
          { label: 'Data Update', route: '/settings/dietech-data-update', description: "Get PSI's latest recipes, USDA ingredients and reference data.", securityField: 'dietech_Data_Update_Level', keywords: 'dietech data update' },
          { label: 'System Log', route: '/system-log', description: 'See who changed what, and when.', securityField: 'dietech_Interaction_Logging_Level', keywords: 'audit' },
          // PSI staff only, as the routes are: they report on the service across every customer.
          { label: 'System Status', route: '/admin/status', description: 'Check service health, background jobs and versions.', securityField: 'user_Level', minLevel: LEVEL.USER_MGMT, psiOnly: true },
          { label: 'Test Kitchen', route: '/admin/feedback', description: 'Review beta findings reported by dietary testers.', securityField: 'user_Level', minLevel: LEVEL.USER_MGMT, psiOnly: true },
        ],
      },
    ],
  },
];

/**
 * Old hub and overview addresses and the section each now opens: App.tsx sends them to that
 * section's main page (or the first page in it the user can open), so bookmarks keep working.
 * `/section/<id>` does the same for every section; `/overview` goes to the dashboard.
 */
export const LEGACY_HUB_REDIRECTS: Record<string, string> = {
  // /settings is not here: it is Setup's own page again (homeRoute), not a redirect.
  '/application-settings': 'setup',
  '/food-data': 'food-data',
  '/clinical-nutrition': 'clinical',
  '/clinical': 'clinical',
  '/nutritional': 'clinical',
  '/operations': 'kitchen',
  '/menucycle-hub': 'menus',
  '/ordering': 'purchasing',
};

/**
 * Menu entries that were folded into another page (Menus, Jeff 2026-11-04), by their old key and the
 * page that holds them now. A pin, a Recent entry or a Feature Access hide saved under the old key
 * goes by the new one (navPins.ts, Sidebar). Their addresses redirect (menuPages.ts movedMenuAddress).
 */
export const MOVED_PAGE_KEYS: Record<string, string> = {
  '/menucycle/requests': MENU_CHANGES_ROUTE,
  '/menucycle/request-change': MENU_CHANGES_ROUTE,
  '/menucycle/import': NEW_MENU_ROUTE,
  '/menucycle/builder': NEW_MENU_ROUTE,
  // The Recycle Bin is Menu Cycles' Deleted menus button.
  '/menucycle/recyclebin': '/menucycle',
};

/** Saved page keys with the moved ones replaced by their new page, each once, in order. */
export function currentPageKeys(keys: readonly string[]): string[] {
  const out: string[] = [];
  for (const key of keys) {
    const now = MOVED_PAGE_KEYS[key] ?? key;
    if (!out.includes(now)) out.push(now);
  }
  return out;
}

/** The address that opens a section (redirects to its main page). Also its breadcrumb. */
export function sectionPath(id: string): string {
  return `/section/${id}`;
}

/** A section's breadcrumb: its own page where it has one (Setup's /settings), else its section address. */
export function sectionCrumb(section: Pick<NavSectionConfig, 'id' | 'homeRoute'>): string {
  return section.homeRoute ?? sectionPath(section.id);
}

/**
 * The key Feature Access hides a whole section by. Purchasing keeps `/purchasing`, the key its
 * old menu entry was hidden by, so an existing hide still applies (its pages moved to /ordering
 * on 2026-10-23; the saved key did not).
 */
export function sectionHideKey(id: string): string {
  return id === 'purchasing' ? '/purchasing' : sectionPath(id);
}

/**
 * Pages that are not in the menu but that the top-bar search still finds. `psiOnly` ones are found
 * by PSI staff only, matching their PsiOnlyRoute guard in App.tsx.
 */
export const SEARCH_ONLY_PAGES: { label: string; route: string; description: string; keywords?: string; psiOnly?: boolean }[] = [
];

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

export interface NavPageRef {
  section: NavSectionConfig;
  group: NavGroupConfig;
  page: NavPageConfig;
}

/** Every page in the config, with its section and group. */
export function allNavPages(sections: readonly NavSectionConfig[] = NAV_SECTIONS): NavPageRef[] {
  return sections.flatMap((section) => section.groups.flatMap((group) => group.pages.map((page) => ({ section, group, page }))));
}

/** The path a page's route resolves to for this facility. */
export function resolveRoute(page: Pick<NavPageConfig, 'route' | 'fallbackRoute'>, facilityId: number | null | undefined): string {
  if (!page.route.includes('{facilityId}')) return page.route;
  return facilityId ? page.route.replace('{facilityId}', String(facilityId)) : page.fallbackRoute ?? page.route.replace('/{facilityId}', '');
}

/** True when `pathname` is `prefix` or below it. */
function within(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix.endsWith('/') ? prefix : prefix + '/');
}

/**
 * The section and page a path belongs to: the page whose route (or an `alsoMatches` path) is the
 * longest prefix of it, so `/items/usda-matcher` is USDA Matcher, not Items, and `/people/27237` is
 * Residents. A `/section/<id>` path belongs to its section with no page. A cross-listed entry is
 * skipped: the address belongs to the page's home section (entrySection says when it does not).
 */
export function locate(
  pathname: string,
  facilityId: number | null | undefined,
  sections: readonly NavSectionConfig[] = NAV_SECTIONS,
): { section: NavSectionConfig; page?: NavPageConfig } | null {
  let best: { section: NavSectionConfig; page?: NavPageConfig } | null = null;
  let bestLen = -1;
  for (const section of sections) {
    if (pathname === sectionPath(section.id) || pathname === section.homeRoute) return { section };
    for (const group of section.groups) {
      for (const page of group.pages) {
        if (page.crossListed) continue;
        for (const path of [resolveRoute(page, facilityId), ...(page.alsoMatches ?? [])]) {
          if (path.includes('{') || !within(pathname, path) || path.length <= bestLen) continue;
          best = { section, page };
          bestLen = path.length;
        }
      }
    }
  }
  if (best) return best;
  // An old hub address that has not redirected yet still belongs to its section.
  const legacy = LEGACY_HUB_REDIRECTS[pathname];
  const legacySection = legacy ? sections.find((x) => x.id === legacy) : undefined;
  return legacySection ? { section: legacySection } : null;
}

/**
 * Whether Feature Access still shows a path that is not itself a menu entry (a dashboard quick
 * action): its own key, the page it opens, and at least one section listing that page must all be
 * shown. An old hub address (`/menucycle-hub`) goes by its section, so hiding Menus hides the Menu
 * Cycle button too. A path the menu does not know goes by its own key alone.
 */
export function shownByFeatureAccess(
  path: string,
  facilityId: number | null | undefined,
  isVisible: (key: string) => boolean,
): boolean {
  if (!isVisible(path)) return false;
  const hit = locate(path, facilityId);
  if (!hit) return true;
  if (!hit.page) return isVisible(sectionHideKey(hit.section.id));
  if (!isVisible(hit.page.route)) return false;
  const listedIn = allNavPages().filter((x) => x.page.route === hit.page!.route).map((x) => sectionHideKey(x.section.id));
  return listedIn.some(isVisible);
}

/** The section the user opened a page from (navEntry.ts): the page's key and the section's id. */
export interface NavEntry {
  key: string;
  sectionId: string;
}

/**
 * The section a page is shown in when the user opened it from a cross-listing (Residents >
 * Selective Touch): that section, if it really lists the page. Null otherwise, and the page stays in
 * its home section (locate). `pageKey` is the config route of the page the user is on now.
 */
export function entrySection(
  pageKey: string | null | undefined,
  entry: NavEntry | null,
  sections: readonly NavSectionConfig[] = NAV_SECTIONS,
): NavSectionConfig | null {
  if (!pageKey || !entry || entry.key !== pageKey) return null;
  const section = sections.find((s) => s.id === entry.sectionId);
  return section?.groups.some((g) => g.pages.some((p) => p.crossListed && p.route === pageKey)) ? section : null;
}

// ---------------------------------------------------------------------------
// Who may see what
// ---------------------------------------------------------------------------

/** Everything the filtering needs to know about the user and facility. */
export interface NavContext {
  /** False until the role has loaded; nothing is shown before then (fails closed). */
  roleLoaded: boolean;
  getLevel: (securityField: string) => number;
  isPsi: boolean;
  facilityId: number | null;
  purchasingLicensed: boolean;
  /** The facility's optional purchasing features; all off until they load. */
  purchasingFeatures: PurchasingFacilityFeatures;
  kdsEnabled: boolean;
  /** Facility settings have loaded (EMR waits for them). */
  settingsLoaded: boolean;
  ehrActive: boolean;
  isCorporate: boolean;
  /** For routeAllowedForFacility (Basic facility = UseProduction 0). */
  facilitySettings: { useProduction?: boolean | null } | null | undefined;
  assessmentsOff: boolean;
  /** The licence is Assessment Only (PM_Allowed_Modules.Dietech_Assessment_Only). Unknown = false. */
  assessmentOnly?: boolean;
  /** Feature Access: true when the user has hidden this page or section key. */
  isHidden: (key: string) => boolean;
  labels: { residentPlural: string; globalMealPattern: string; ehrTitle: string };
  /**
   * The route guard's staged rule for a security field (components/common/stagedRouteRules.ts):
   * false only in Enforce mode when the role misses it. Absent = no staged rules.
   */
  stagedRouteAllowed?: (securityField: string) => boolean;
}

export interface NavPageView {
  label: string;
  route: string;
  description: string;
  /** The config route: the key Feature Access hides the page by. */
  key: string;
  disabledReason?: string;
  keywords?: string;
  /** Feature Access cannot hide it. */
  alwaysShown?: boolean;
  /** A second listing of a page whose home is another section (NavPageConfig.crossListed). */
  crossListed?: boolean;
}

export interface NavGroupView {
  title: string;
  pages: NavPageView[];
}

export interface NavSectionView {
  id: string;
  label: string;
  icon: string;
  /** Feature Access key for the whole section. */
  hideKey: string;
  /** Where the rail goes: the main page, or the first page in the section the user can open. */
  railRoute: string;
  groups: NavGroupView[];
  pages: NavPageView[];
}

function labelFor(item: { label: string; dynamicLabel?: NavLabelKey }, ctx: Pick<NavContext, 'labels'>): string {
  switch (item.dynamicLabel) {
    case 'residentPlural': return ctx.labels.residentPlural;
    case 'manageResidents': return `Manage ${ctx.labels.residentPlural}`;
    case 'globalMealPattern': return ctx.labels.globalMealPattern;
    case 'ehrTitle': return ctx.labels.ehrTitle;
    default: return item.label;
  }
}

/** Whether the section as a whole is open to this user (its own gate, not its pages). */
export function sectionAllowed(section: NavSectionConfig, ctx: NavContext): boolean {
  if (!ctx.roleLoaded) return false;
  if (section.licence === 'purchasing' && !ctx.purchasingLicensed) return false;
  if (section.securityField && ctx.getLevel(section.securityField) < 1) return false;
  return true;
}

/**
 * Whether the role, licence and facility allow the page: the same answer its route guard gives,
 * plus the facility conditions the menu applies. Feature Access hides are NOT applied here.
 */
export function pageAllowed(page: NavPageConfig, ctx: NavContext): boolean {
  if (!ctx.roleLoaded) return false;
  if (page.anyOf && !page.anyOf.some((gate) => pageAllowed({ ...page, anyOf: undefined, ...gate }, ctx))) return false;
  if (page.psiOnly && !ctx.isPsi) return false;
  if (page.licence === 'purchasing' && !ctx.purchasingLicensed) return false;
  if (page.purchasingFeature && !featureOn(ctx.purchasingFeatures, page.purchasingFeature)) return false;
  if (page.securityField && ctx.getLevel(page.securityField) < (page.minLevel ?? LEVEL.VIEW) && !(page.psiStaffToo && ctx.isPsi)) return false;
  if (page.securityField && ctx.stagedRouteAllowed && !ctx.stagedRouteAllowed(page.securityField)) return false;
  if (!routeAllowedForFacility(page.route, ctx.facilitySettings)) return false;
  for (const condition of page.when ?? []) {
    if (condition === 'corporate' && !ctx.isCorporate) return false;
    if (condition === 'kds' && !ctx.kdsEnabled) return false;
    if (condition === 'production' && ctx.facilitySettings?.useProduction === false) return false;
    if (condition === 'assessments' && ctx.assessmentsOff) return false;
    if (condition === 'notAssessmentOnly' && ctx.assessmentOnly) return false;
    // EMR waits for facility settings, so it never flashes in the wrong state.
    if (condition === 'ehr' && !ctx.settingsLoaded) return false;
  }
  return true;
}

/** The EHR page's name when the facility's EHR is off (Jeff, 2026-10-25). */
export const EMR_OFF_LABEL = 'EMR Integration';

function pageView(page: NavPageConfig, ctx: NavContext): NavPageView {
  const ehrOff = !!page.when?.includes('ehr') && !ctx.ehrActive;
  // With the facility's EHR off, the page is named generically, not after a vendor the facility doesn't use.
  const label = ehrOff && page.dynamicLabel === 'ehrTitle' ? EMR_OFF_LABEL : labelFor(page, ctx);
  const residents = ctx.labels.residentPlural.toLowerCase();
  const view: NavPageView = {
    label,
    route: resolveRoute(page, ctx.facilityId),
    description: page.description.replace(/\{residents\}/g, residents),
    key: page.route,
  };
  if (page.keywords) view.keywords = page.keywords;
  if (page.alwaysShown) view.alwaysShown = true;
  if (page.crossListed) view.crossListed = true;
  /* Greyed out with the reason, not hidden, where the facility's EHR is off: people could not
     find a menu item that simply vanished (Jeff, 2026-09-28). */
  if (ehrOff) view.disabledReason = 'Not enabled for this facility';
  return view;
}

/**
 * The sections, groups and pages this user may see, in order. Empty groups and sections are
 * dropped; Feature Access hides apply unless `applyHides` is false (the dialog lists everything the
 * role allows so it can offer the checkbox back).
 */
export function buildNavSections(ctx: NavContext, { applyHides = true } = {}): NavSectionView[] {
  if (!ctx.roleLoaded) return [];
  const out: NavSectionView[] = [];
  for (const section of NAV_SECTIONS) {
    if (!sectionAllowed(section, ctx)) continue;
    if (applyHides && ctx.isHidden(sectionHideKey(section.id))) continue;
    const groups: NavGroupView[] = section.groups
      .map((group) => ({
        title: group.title,
        pages: group.pages
          .filter((page) => pageAllowed(page, ctx) && (!applyHides || page.alwaysShown || !ctx.isHidden(page.route)))
          .map((page) => pageView(page, ctx)),
      }))
      .filter((group) => group.pages.length > 0);
    const pages = groups.flatMap((g) => g.pages);
    if (pages.length === 0) continue;
    const enabled = pages.filter((p) => !p.disabledReason);
    const main = enabled.find((p) => p.key === section.mainRoute) ?? enabled[0] ?? pages[0];
    out.push({
      id: section.id,
      label: labelFor(section, ctx),
      icon: section.icon,
      hideKey: sectionHideKey(section.id),
      railRoute: section.homeRoute ?? main.route,
      groups,
      pages,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

/**
 * Breadcrumb parents and labels the config implies: each page sits under its section (Menus >
 * Menu Cycles), whose crumb opens the section's main page, and the page's crumb is its menu label,
 * word for word (Jeff, 2026-10-25: the breadcrumb is the page title, so it says what the menu says).
 * The parent is skipped where the page has the section's own name (Reports > Reports), for Home,
 * and for a cross-listed entry: the page's crumb is its home section (TopToolbar swaps it while the
 * user is on it from the other listing). A page whose route has a `{token}` (Facility Settings) is
 * left to TopToolbar, which knows the facility. TopToolbar merges these over its static table, and
 * fills the labels a facility words its own way (dynamicLabel).
 */
export function navBreadcrumbs(): { labels: Record<string, string>; parents: Record<string, string> } {
  const labels: Record<string, string> = {};
  const parents: Record<string, string> = {};
  for (const section of NAV_SECTIONS) {
    // A section with a page of its own (Setup's /settings) is that page in the trail.
    const crumb = sectionCrumb(section);
    if (section.id !== 'home') labels[crumb] = section.label;
    for (const group of section.groups) {
      for (const page of group.pages) {
        if (page.route.includes('{') || page.crossListed) continue;
        labels[page.route] = page.label;
        if (section.id === 'home' || page.label === section.label) continue;
        parents[page.route] = crumb;
      }
    }
  }
  for (const page of SEARCH_ONLY_PAGES) labels[page.route] = page.label;
  return { labels, parents };
}

// ---------------------------------------------------------------------------
// Top-bar search
// ---------------------------------------------------------------------------

/** What the command palette needs per entry (commandPalette.ts NavSource). */
export interface NavSearchSource {
  label: string;
  icon: string;
  route: string;
  subtitle?: string;
  keywords?: string;
}

/**
 * The command palette's page list. `directory` is what it lists before anything is typed (the
 * sections, as the rail has them); `searchable` is every page the menu shows this user, by label,
 * with its section and description as the second line, plus the search-only pages. Pages shown
 * greyed out (EMR with EHR off) are left out: they cannot be opened. A page listed in two sections is
 * listed once, under its home section (or under the other when the user has hidden its home). A
 * PSI-only search page is listed only when `isPsi` (useIsPsiStaff).
 */
export function navSearchSources(sections: readonly NavSectionView[], isPsi = false): { directory: NavSearchSource[]; searchable: NavSearchSource[] } {
  const directory = sections.map((s) => ({ label: s.label, icon: s.icon, route: s.railRoute }));
  const seen = new Set<string>();
  const searchable: NavSearchSource[] = [];
  // Home listings first, then cross-listings whose home the user cannot see.
  for (const crossListed of [false, true]) {
    for (const s of sections) {
      for (const p of s.pages) {
        if (!!p.crossListed !== crossListed || p.disabledReason || seen.has(p.route)) continue;
        seen.add(p.route);
        searchable.push({ label: p.label, icon: s.icon, route: p.route, subtitle: `${s.label}: ${p.description}`, keywords: p.keywords });
      }
    }
  }
  if (sections.length > 0) {
    for (const p of SEARCH_ONLY_PAGES) {
      if (seen.has(p.route) || (p.psiOnly && !isPsi)) continue;
      seen.add(p.route);
      searchable.push({ label: p.label, icon: 'bi-chat-dots', route: p.route, subtitle: p.description, keywords: p.keywords });
    }
  }
  return { directory, searchable };
}
